/**
 * Presentation rules for Amplitude flags.
 *
 * Categories come from the repo's own lint rule
 * (`packages/eslint-plugin-cpos/rules/amplitude-flag-naming.js`), so this map
 * stays true to the convention rather than to a snapshot of today's flags.
 * Several flags predate that rule, which is why "uncategorised" is a real
 * state here and not a silent fallback into some other colour.
 */

export const FLAG_CATEGORIES = [
  "release",
  "fix",
  "experiment",
  "ops",
  "test",
  "refactor",
] as const;

export type FlagCategory = (typeof FLAG_CATEGORIES)[number] | "uncategorised";

const CATEGORY_STYLES = new Map<FlagCategory, string>([
  ["release", "bg-blue-50 text-blue-700 ring-blue-200"],
  ["fix", "bg-amber-50 text-amber-800 ring-amber-200"],
  ["experiment", "bg-violet-50 text-violet-700 ring-violet-200"],
  ["ops", "bg-teal-50 text-teal-700 ring-teal-200"],
  ["test", "bg-pink-50 text-pink-700 ring-pink-200"],
  ["refactor", "bg-muted text-muted-foreground ring-border"],
  ["uncategorised", "bg-muted text-muted-foreground ring-border ring-dashed"],
]);

/**
 * The convention is `<category>-<slug>`, so a bare category word is not a
 * categorised flag — treating it as one would render a pill with an empty
 * name beside it.
 */
export function flagCategory(key: string): FlagCategory {
  const separator = key.indexOf("-");
  if (separator <= 0 || separator === key.length - 1) return "uncategorised";

  const prefix = key.slice(0, separator);
  return (FLAG_CATEGORIES as readonly string[]).includes(prefix)
    ? (prefix as FlagCategory)
    : "uncategorised";
}

export function categoryStyle(category: FlagCategory): string {
  return CATEGORY_STYLES.get(category) ?? "";
}

/** The key with its category prefix removed, so the pill isn't repeated in the name. */
export function flagLabel(key: string): string {
  const category = flagCategory(key);
  return category === "uncategorised" ? key : key.slice(category.length + 1);
}

export type VariantKind = "on" | "off" | "unresolved" | "payload";

/**
 * What to show as the flag's value.
 *
 * Amplitude keeps a flag's configured value in the payload and leaves
 * `value` as a plain on/off — `ops-mobile-min-supported-version` reports
 * `on` and carries the minimum version in the payload. The payload is the
 * answer whenever there is one.
 */
export function effectiveVariant(flag: {
  serverVariant: string | null;
  serverPayload: string | null;
}): string | null {
  return flag.serverPayload ?? flag.serverVariant;
}

/**
 * How to render a variant value.
 *
 * `unresolved` is deliberately distinct from `off`: a flag nobody has
 * evaluated and a flag evaluated to off look identical in the raw data, and
 * conflating them is what sends people down the wrong path.
 */
export function variantKind(value: string | null | undefined): VariantKind {
  if (value === null || value === undefined || value === "")
    return "unresolved";

  const normalized = value.toLowerCase();
  if (normalized === "on" || normalized === "true" || normalized === "1") {
    return "on";
  }
  if (normalized === "off" || normalized === "false" || normalized === "0") {
    return "off";
  }
  return "payload";
}

const VARIANT_STYLES = new Map<VariantKind, string>([
  ["on", "bg-emerald-50 text-emerald-700 ring-emerald-200"],
  ["off", "bg-muted text-muted-foreground ring-border"],
  ["unresolved", "bg-amber-50 text-amber-700 ring-amber-300 ring-dashed"],
  ["payload", "bg-indigo-50 text-indigo-700 ring-indigo-200"],
]);

export function variantStyle(kind: VariantKind): string {
  return VARIANT_STYLES.get(kind) ?? "";
}

export function variantLabel(
  value: string | null | undefined,
  kind: VariantKind,
): string {
  if (kind === "unresolved") return "–";
  return value ?? "–";
}

/** Payload variants carry a value worth reading, so they get an expander. */
export function isExpandable(
  kind: VariantKind,
  deviceVariant: string | null,
): boolean {
  return kind === "payload" || deviceVariant !== null;
}
